/*
 * Copyright (c) 2022. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import * as z from 'zod';
import { compoundVersionIdSchemaZ } from '../components';
export const granularities = ['1h', '30min', '15min', '10min', '5min'];
export const auxUsages = ['count', 'meet', 'no_aux'];
export const operationTypes = [
    'Price Dispatch',
    'Fixed Shape',
    'Fixed Shape with Price Dispatch',
    'As Available without Clipping Capture',
    'As Available with Clipping Capture',
];
export const shapeObjectives = [
    'minimize_deficit',
    'minimize_cost',
    'Minimize Deficit',
    'Minimize Net Cost',
];
export const plantSimSingleRunGranularitySchema = z.enum(granularities);
export const plantSimSingleRunAggregationMethodSchema = z.enum([
    'mean',
    'sum',
    'minimum',
    'maximum',
]);
export const plantSimSingleRunOperationTypeSchema = z.enum(operationTypes);
export const plantSimSingleRunShapeObjectiveSchema = z.enum(shapeObjectives).nullable();
export const plantSimSingleRunAuxUsageSchema = z.enum(auxUsages).nullable();
export const plantSimSingleRunMetricSchema = z.union([
    z.enum([
        'poi.plant_out',
        'poi.power_in',
        'poi.power_out',
        'poi.clipped',
        'shape.profile',
        'shape.excess',
        'shape.deficit',
        'shape.delivery',
        'shape.50_pct_deficit',
        'shape.90_pct_deficit',
        'shape.normalized_delivery',
        'shape.normalized_deficit',
        'shape.deficit_cost',
        'shape.excess_revenue',
        'shape.buy_price',
        'shape.sell_price',
        'price_dispatch.sell_price',
        'price_dispatch.buy_price',
        'price_dispatch.sell_power',
        'price_dispatch.buy_power',
        'price_dispatch.revenue',
        'pv.power_out',
        'wind.power_out',
    ]),
    z.string().regex(/^bess-\d+\.(charge|discharge)$/),
]);
export const plantSimSingleRunMerchantPricesOptionSchema = z.enum([
    'data_8760',
    'multiyear_prices',
    'price_crack',
    'Market Prices',
    'Multi-Year Prices',
    'Price Crack',
]);
export const plantSimSingleRunScaledEnergyDataRequestSchema = z.strictObject({
    id: z.uuid(),
    data_start_year: z.number().int().nullable().optional(),
    shape_scale: z.number().gt(0).nullable().optional(),
});
const plantSimSingleRunShapePenaltyMatrixSchema = z
    .array(z.array(z.number()))
    .refine((value) => value.length === 12 &&
    value.every((row) => [24, 48, 96, 144, 288].includes(row.length)), 'Shape values must be 12 x (24 * intervals per hour)');
const plantSimSingleRunShapeValueSchema = z
    .union([z.number(), plantSimSingleRunShapePenaltyMatrixSchema])
    .nullable()
    .optional();
export const plantSimSingleRunAggregationMetricSchema = z
    .strictObject({
    metric: plantSimSingleRunMetricSchema,
    groupby_month: z.boolean().optional(),
    groupby_blocks: z.boolean().optional(),
    aggregation_methods: z.array(plantSimSingleRunAggregationMethodSchema),
    reliability: z.number().nullable().optional(),
})
    .superRefine((value, ctx) => {
    if (value.metric !== 'shape.deficit' &&
        value.reliability !== null &&
        value.reliability !== undefined) {
        ctx.addIssue({
            code: 'custom',
            message: 'Reliability should be defined only for shape deficit metric',
            path: ['reliability'],
        });
    }
    if (value.metric === 'shape.deficit' && value.reliability === undefined) {
        ctx.addIssue({
            code: 'custom',
            message: 'Reliability must be defined for shape deficit metric',
            path: ['reliability'],
        });
    }
});
export const plantSimSingleRunPostProcessingInputsSchema = z.strictObject({
    aggregation_metrics: z.array(plantSimSingleRunAggregationMetricSchema),
    blocks: z.array(z.array(z.string()).length(24)).length(12).nullable().optional(),
});
export const plantSimSingleRunAustraliaMarketSchema = z
    .strictObject({
    country: z.literal('australia'),
    reg_raise_enablement: z.number().min(0).max(1).optional(),
    reg_lower_enablement: z.number().min(0).max(1).optional(),
    regulation_max: z.number().min(0).max(1).optional(),
    poi_in_max_mw: z.number().gt(0).nullable().optional(),
    poi_out_max_mw: z.number().gt(0).nullable().optional(),
})
    .superRefine((value, ctx) => {
    const noFcas = (value.reg_raise_enablement ?? 0) === 0 &&
        (value.reg_lower_enablement ?? 0) === 0 &&
        (value.regulation_max ?? 0) === 0;
    const noPoiLimits = value.poi_in_max_mw == null && value.poi_out_max_mw == null;
    if (noFcas && noPoiLimits) {
        ctx.addIssue({
            code: 'custom',
            message: 'Either participate in FCAS or set poi_in_max_mw and poi_out_max_mw values',
        });
    }
});
export const plantSimSingleRunProjectInputsSchema = z
    .strictObject({
    cod: z.string(),
    contracted_term: z.number().int(),
    merchant_tail_term: z.number().int(),
    operation_types: z.array(plantSimSingleRunOperationTypeSchema).min(1).max(1),
    shape_objective: plantSimSingleRunShapeObjectiveSchema.optional(),
    granularity: plantSimSingleRunGranularitySchema.optional(),
    sim_horizon: z.union([z.number().int(), z.string()]).optional(),
    aux_usage: plantSimSingleRunAuxUsageSchema.optional(),
    postprocessing: plantSimSingleRunPostProcessingInputsSchema.nullable().optional(),
    ignore_leap_days: z.boolean().optional(),
    market_inputs: plantSimSingleRunAustraliaMarketSchema.nullable().optional(),
    allow_grid_charge: z.boolean().optional(),
})
    .superRefine((value, ctx) => {
    const cod = new Date(value.cod);
    if (Number.isNaN(cod.getTime())) {
        ctx.addIssue({
            code: 'custom',
            message: 'COD must be a valid datetime',
            path: ['cod'],
        });
        return;
    }
    const lastDayOfMonth = new Date(Date.UTC(cod.getUTCFullYear(), cod.getUTCMonth() + 1, 0));
    if (cod.getUTCDate() !== lastDayOfMonth.getUTCDate()) {
        ctx.addIssue({
            code: 'custom',
            message: 'COD must be on last day of the month',
            path: ['cod'],
        });
    }
});
export const plantSimSingleRunPvRequestSchema = z.strictObject({
    id: compoundVersionIdSchemaZ,
    name: z.string(),
    label: z.string(),
    ilr: z.number().gt(0).nullable().optional(),
    n_inverters: z.number().int().min(1).nullable().optional(),
    data_start_year: z.number().int().nullable().optional(),
    module_degradation: z.boolean().nullable().optional(),
    optimize_ptc: z.boolean().nullable().optional(),
    optimize_rec: z.boolean().nullable().optional(),
});
export const plantSimSingleRunBessCustomParametersSchema = z.strictObject({
    usable_capacities_at_poi: z.array(z.number()).nullable(),
    roundtrip_efficiency: z.array(z.number()).nullable(),
    degradation_model: z.enum(['annual', 'weekly', 'daily']).default('weekly'),
});
export const plantSimSingleRunBessRequestSchema = z.strictObject({
    id: compoundVersionIdSchemaZ,
    name: z.string(),
    label: z.string(),
    power: z.number().gt(0).nullable().optional(),
    duration: z.number().gt(0).nullable().optional(),
    throughput_cost: z.number().min(0).nullable().optional(),
    max_annual_cycles: z.number().gt(0).nullable().optional(),
    init_soc: z.number().default(1),
    ramp_cost: z.number().min(0).nullable().optional(),
    idle_aux_mw: z.number().min(0).nullable().optional(),
    active_aux_mw_per_mw: z.number().min(0).max(1).nullable().optional(),
    self_discharge_rate: z.number().min(0).max(1).nullable().optional(),
    custom_parameters: plantSimSingleRunBessCustomParametersSchema.nullable().optional(),
});
export const plantSimSingleRunAsAvailableRequestSchema = z.strictObject({
    id: z.uuid(),
    name: z.string(),
    label: z.string(),
    n_turbines: z.number().int().gt(0).nullable().optional(),
    aux_power_losses: z.number().min(0).nullable().optional(),
    transmission_loss: z.number().nullable().optional(),
    data_start_year: z.number().int().nullable().optional(),
    optimize_ptc: z.boolean().nullable().optional(),
    optimize_rec: z.boolean().nullable().optional(),
});
export const plantSimSingleRunThermalRequestSchema = z
    .strictObject({
    generator_id: z.uuid(),
    name: z.string(),
    label: z.string(),
    heat_rate: z.number().nullable().optional(),
    weather_id: z.uuid().nullable().optional(),
    weather_start_year: z.number().int().nullable().optional(),
    max_inlet_air_temperature: z.number().nullable().optional(),
    design_mw: z.number().min(0).nullable().optional(),
    unit_count: z.number().int().gt(0).nullable().optional(),
    turndown_ratio: z.number().min(0).max(1).nullable().optional(),
    annual_energy_max: z.number().gt(0).nullable().optional(),
    annual_capacity_factor: z.number().gt(0).max(1).nullable().optional(),
    fuel_price_fixed: z.number().nullable().optional(),
    fuel_price_id: z.uuid().nullable().optional(),
    fuel_price_start_year: z.number().int().nullable().optional(),
})
    .superRefine((value, ctx) => {
    if (value.weather_id == null && value.heat_rate == null) {
        ctx.addIssue({
            code: 'custom',
            message: 'Either heat rate or weather id is required',
            path: ['heat_rate'],
        });
    }
    if (value.fuel_price_id == null && value.fuel_price_fixed == null) {
        ctx.addIssue({
            code: 'custom',
            message: 'Either fuel price id or a fixed fuel price should be given',
        });
    }
});
export const plantSimSingleRunShapeRequestSchema = z.strictObject({
    id: z.uuid(),
    name: z.string(),
    label: z.string(),
    data_start_year: z.number().int().nullable().optional(),
    shape_scale: z.number().gt(0).nullable().optional(),
    deficit_penalty: plantSimSingleRunShapeValueSchema,
    excess_revenue: plantSimSingleRunShapeValueSchema,
});
export const plantSimSingleRunPriceIdsSchema = z
    .strictObject({
    merchant_price_option: plantSimSingleRunMerchantPricesOptionSchema,
    market_price_id: z.uuid().nullable().optional(),
    multiyear_price_id: z.uuid().nullable().optional(),
    onoffpeak_price_id: z.uuid().nullable().optional(),
    data_start_year: z.number().int().nullable().optional(),
})
    .superRefine((value, ctx) => {
    if ((value.merchant_price_option === 'data_8760' ||
        value.merchant_price_option === 'Market Prices') &&
        value.market_price_id == null) {
        ctx.addIssue({
            code: 'custom',
            message: `Market price id must be provided for merchant_price_option:${value.merchant_price_option}`,
        });
    }
    if ((value.merchant_price_option === 'multiyear_prices' ||
        value.merchant_price_option === 'Multi-Year Prices') &&
        (value.multiyear_price_id == null || value.data_start_year == null)) {
        ctx.addIssue({
            code: 'custom',
            message: `Multiyear price id and data start year must be provided for merchant_price_option:` +
                value.merchant_price_option,
        });
    }
    if ((value.merchant_price_option === 'price_crack' ||
        value.merchant_price_option === 'Price Crack') &&
        (value.market_price_id == null || value.onoffpeak_price_id == null)) {
        ctx.addIssue({
            code: 'custom',
            message: 'Both market price id and on/off peak price id must be provided for ' +
                `merchant_price_option:${value.merchant_price_option}`,
        });
    }
});
export const plantSimSingleRunBuySellMarketsSchema = z.strictObject({
    buy_market: plantSimSingleRunPriceIdsSchema,
    sell_market: plantSimSingleRunPriceIdsSchema,
});
export const plantSimSingleRunRegMarketSchema = z.strictObject({
    reg_raise_market: plantSimSingleRunPriceIdsSchema,
    reg_lower_market: plantSimSingleRunPriceIdsSchema,
});
export const plantSimSingleRunFixedPriceInputsSchema = z.strictObject({
    base_rate: z.number(),
    escalation_rate: z.number(),
});
export const plantSimSingleRunPtcInputsSchema = z.strictObject({
    base_rate: z.number(),
    escalation_rate: z.number(),
    base_year: z.number().int(),
    tenor: z.number().int(),
    pv_eligible: z.boolean().optional(),
    wind_eligible: z.boolean().optional(),
});
export const plantSimSingleRunRecInputsSchema = z.strictObject({
    price_id: z.string().nullable(),
    fixed_price: plantSimSingleRunFixedPriceInputsSchema.nullable(),
    pv_eligible: z.boolean().optional(),
    wind_eligible: z.boolean().optional(),
});
export const plantSimSingleRunPricesRequestSchema = z.strictObject({
    energy_market: plantSimSingleRunBuySellMarketsSchema.nullable().optional(),
    reg_market: plantSimSingleRunRegMarketSchema.nullable().optional(),
    rec_market: plantSimSingleRunRecInputsSchema.nullable().optional(),
    ptc: plantSimSingleRunPtcInputsSchema.nullable().optional(),
});
export const plantSimSingleRunVariableFeederRequestSchema = z.strictObject({
    poi_in_limits: plantSimSingleRunScaledEnergyDataRequestSchema.nullable().optional(),
    poi_out_limits: plantSimSingleRunScaledEnergyDataRequestSchema.nullable().optional(),
});
export const plantSimSingleRunPoiFeederRequestSchema = z.strictObject({
    power_rating_mw: z.number().gt(0),
    losses: z.number().min(0).max(1).optional(),
    allow_charge: z.boolean(),
    variable_limits: plantSimSingleRunVariableFeederRequestSchema.nullable().optional(),
});
export const plantSimSingleRunMvBusFeederRequestSchema = z.strictObject({
    power_rating_mw: z.number().gt(0).nullable().optional(),
    losses: z.number().min(0).max(1).optional(),
    allow_charge: z.boolean(),
});
export const plantSimSingleRunMvBusRequestSchema = z.strictObject({
    name: z.string(),
    label: z.string(),
    feeder: plantSimSingleRunMvBusFeederRequestSchema,
    pv: z.array(plantSimSingleRunPvRequestSchema).nullable().optional(),
    bess: z.array(plantSimSingleRunBessRequestSchema).nullable().optional(),
    as_available: z.array(plantSimSingleRunAsAvailableRequestSchema).nullable().optional(),
    shape: z.array(plantSimSingleRunShapeRequestSchema).nullable().optional(),
    thermal: z.array(plantSimSingleRunThermalRequestSchema).nullable().optional(),
});
export const plantSimSingleRunRequestSchema = z
    .strictObject({
    project: plantSimSingleRunProjectInputsSchema,
    poi: plantSimSingleRunPoiFeederRequestSchema,
    mv_bus: z.array(plantSimSingleRunMvBusRequestSchema),
    prices: plantSimSingleRunPricesRequestSchema.nullable().optional(),
    shape: z.array(plantSimSingleRunShapeRequestSchema).nullable().optional(),
    plantsim_version: z.number().int().default(2),
})
    .superRefine((value, ctx) => {
    const shapeCount = (value.shape?.length ?? 0) +
        value.mv_bus.reduce((count, bus) => count + (bus.shape?.length ?? 0), 0);
    if (shapeCount > 1) {
        ctx.addIssue({
            code: 'custom',
            message: 'Only one shape is allowed. This shape can be either FTM or BTM.',
        });
    }
    const operationType = value.project.operation_types[0];
    const hasBtmShape = value.mv_bus.some((bus) => Boolean(bus.shape?.length));
    const hasShape = hasBtmShape || Boolean(value.shape?.length);
    const isShapeMode = operationType === 'Fixed Shape';
    const isPriceDispatchMode = operationType === 'Price Dispatch';
    const isShapeWithPriceDispatchMode = operationType === 'Fixed Shape with Price Dispatch';
    if (isShapeMode && !hasShape) {
        ctx.addIssue({
            code: 'custom',
            message: 'Shape parameters are required for fixed shape operation mode',
        });
    }
    if (isPriceDispatchMode && value.prices == null) {
        ctx.addIssue({
            code: 'custom',
            message: 'Prices are required for price dispatch operation mode',
        });
    }
    if (isShapeWithPriceDispatchMode && !hasShape) {
        ctx.addIssue({
            code: 'custom',
            message: 'Shape parameters are required for shape with price dispatch operation mode',
        });
    }
    if (isShapeWithPriceDispatchMode && value.prices == null) {
        ctx.addIssue({
            code: 'custom',
            message: 'Prices are required for shape with price dispatch operation mode',
        });
    }
    if (hasBtmShape && !value.poi.allow_charge) {
        ctx.addIssue({
            code: 'custom',
            message: 'Grid charge must be allowed for BTM shape',
        });
    }
});
export const plantSimSingleRunFlowParametersSchema = z
    .strictObject({
    config_id: compoundVersionIdSchemaZ.optional(),
    request: plantSimSingleRunRequestSchema.optional(),
})
    .superRefine((value, ctx) => {
    if (!value.config_id && !value.request) {
        ctx.addIssue({
            code: 'custom',
            message: 'Either config_id or request is required',
            path: ['request'],
        });
    }
});
//# sourceMappingURL=single-run-schema.js.map